import React, { useCallback, useState } from "react";
import * as Scrivito from "scrivito";
import { show } from "@ebay/nice-modal-react";
import {
  Checkbox,
  SortButton,
  UpdateProjectStatusModal,
  VirtualizedTable,
} from "components";
import { ConfirmationModal } from "components/_Modals/ConfirmationModal";
import { useProjectListContext } from "objs/_ConfiguratorObjs/ProjectList/hooks/useProjectListContext";
import { MultipleProjectsDeleteMut } from "api/mutations";
import { useProjectActions } from "stores/project";
import { useTranslationHelpers } from "../../hooks/useTranslationHelpers";
import { ProjectListTableRow } from "./ProjectListTableRow";
import { useLanguage } from "stores/globalStore";

export const ProjectListTableBody = Scrivito.connect(() => {
  const {
    pagesCount,
    itemsPerPage,
    currentPageNo,
    error,
    isLoading,
    isOrderDescending,
    projects,
    setIsOrderDescending,
    refetchProjectList,
    projectsToDelete,
    setProjectsToDelete,
    resetUrlParams,
    projectsCount,
    setCurrentPageNo,
    isSelectAll,
    setIsSelectAll,
    projectListQueryParams,
  } = useProjectListContext();
  const language = useLanguage();

  const actions = useProjectActions();

  const [spinnerId, setSpinnerId] = useState();
  const [statusOverrides, setStatusOverrides] = useState({});
  const translations = useTranslationHelpers();
  const [project, setProject] = useState({});
  const { projectId } = Scrivito.currentPageParams();
  const toggleProjectMenu = useCallback((projectData) => {
    if (projectData.id) {
      setProject(projectData);
    }
  }, []);

  const toggleSelectAll = () => {
    if (!isSelectAll) {
      setProjectsToDelete(projects.map((proj) => proj.id));
    } else {
      setProjectsToDelete([]);
    }
    setIsSelectAll(!isSelectAll);
  };

  const handleCheckboxClick = (itemId) => {
    const updatedCheckboxes = projectsToDelete.includes(itemId)
      ? projectsToDelete.filter((id) => id !== itemId)
      : [...projectsToDelete, itemId];
    setProjectsToDelete(updatedCheckboxes);
    setIsSelectAll(updatedCheckboxes.length === projects.length);
  };

  const toggleModalStatus = useCallback(() => {
    show(UpdateProjectStatusModal, { project, projectListQueryParams }).then(
      (result) => {
        const updatedProjectId = result?.projectId || project?.id;

        if (updatedProjectId && result?.status) {
          setStatusOverrides((prev) => ({
            ...prev,
            [updatedProjectId]: result.status,
          }));
        }
      },
    );
    toggleProjectMenu({});
  }, [project, projectListQueryParams, toggleProjectMenu]);

  const toggleDeleteModal = useCallback(() => {
    toggleProjectMenu({});
    show(ConfirmationModal, {
      submitBtnText: translations.PROJECT_MODAL_DELETE_TEXT,
      title: translations.PROJECT_MODAL_DELETE_TEXT,
      alertText: translations.PROJECT_MODAL_DELETE_WARNING_TEXT_WITH_TITLE(
        project.title,
      ),
      mutation: MultipleProjectsDeleteMut,
      params: { input: { ids: [project?.id], languageCode: language } },
    }).then(() => {
      setSpinnerId(project?.id);
      setTimeout(() => {
        refetchProjectList({
          requestPolicy: "network-only",
        });
        if (projectId && project?.id === projectId) {
          actions.clear();
          resetUrlParams();
        }
        setSpinnerId(null);
      }, 2000);
    });
  }, [
    toggleProjectMenu,
    translations,
    project.title,
    project?.id,
    language,
    refetchProjectList,
    projectId,
    actions,
    resetUrlParams,
  ]);

  const skeletonCount =
    +currentPageNo !== +pagesCount
      ? +itemsPerPage
      : +projectsCount - +itemsPerPage * (+currentPageNo - 1);

  const columns = [
    {
      label: (
        <div className="gap-3">
          <Checkbox
            onChange={toggleSelectAll}
            checked={isSelectAll}
            wrapperClassName="me-3"
          />
          {translations.PROJECT_LIST_PROJECT}
        </div>
      ),
      width: "25%",
      dataTestid: "PROJECT_LIST_PROJECT",
    },
    {
      label: (
        <div
          className="d-flex justify-content-between position-relative"
          data-testid="PROJECT_LIST_LAST_MODIFICATION"
        >
          {translations.PROJECT_LIST_LAST_MODIFICATION}
          <SortButton
            sortCriteria="PROJECT_LIST_LAST_MODIFICATION"
            currentSort="PROJECT_LIST_LAST_MODIFICATION"
            isOrderDescending={isOrderDescending}
            setIsOrderDescending={setIsOrderDescending}
            setCurrentPageNo={setCurrentPageNo}
            setIsSelectAll={setIsSelectAll}
            setProjectsToDelete={setProjectsToDelete}
          />
        </div>
      ),
      width: "20%",
    },
    {
      label: translations.PROJECT_SHARING_SETTINGS,
      width: "20%",
      dataTestid: "PROJECT_SHARING_SETTINGS",
    },
    {
      label: translations.PROJECT_LIST_STATUS,
      width: "35%",
      dataTestid: "PROJECT_LIST_STATUS",
    },
  ];

  const renderRow = (data) => (
    <ProjectListTableRow
      key={data.id}
      toggleProjectMenu={toggleProjectMenu}
      toggleModalStatus={toggleModalStatus}
      toggleDeleteModal={toggleDeleteModal}
      handleCheckboxClick={handleCheckboxClick}
      isChecked={projectsToDelete.includes(data.id)}
      project={{
        ...data,
        status: statusOverrides[data.id]
          ? { ...data.status, status: statusOverrides[data.id] }
          : data.status,
      }}
      translations={translations}
      spinnerId={spinnerId}
    />
  );

  const renderNoData = () => (
    <tr>
      <td
        className="text-center"
        colSpan="5"
        data-testid="PROJECT_LIST_NO_DATA"
      >
        {translations.PROJECT_LIST_NO_DATA}
      </td>
    </tr>
  );

  if (error) {
    return (
      <div className="mb-6">
        <span>{translations.ERROR_OCCURED}</span>
      </div>
    );
  }

  return (
    <VirtualizedTable
      columns={columns}
      list={projects}
      renderRow={renderRow}
      renderNoData={renderNoData}
      isLoading={isLoading}
      skeletonCount={skeletonCount}
      accessId={(item) => item?.id}
    />
  );
});
